import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash.isequal';
import { Button, Icon, Intent } from '@blueprintjs/core';
import { connect, ConnectedProps } from 'react-redux';
import Draggable from 'react-draggable';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as alertNotificationSelectors from 'data/alertNotification/alertNotification.selectors';
import * as alertNotificationActions from 'data/alertNotification/alertNotification.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { AlertNotificationModel } from 'data/alertNotification/alertNotification.types';
import { AuditDetails, WithAuditDetails } from 'data/types';
import {
 AuditActionType, AuditService, getAuditDetails, getAuditFieldInfo
} from 'shared/userAudit/audit-constants';
import AlertNotificationCollapseExpand from './components/AlertNotificationCollapseExpand';
import './alert-notification.scss';
import { getAlarmTypes } from './utils/getAlarmTypes';

const mapState = (state: RootState) => ({
  isAuthenticated: authenticationSelectors.isAuthenticated(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  alertNotifications: alertNotificationSelectors.getAlertNotifications(state),
  isAlertNotificationEnabled: discoverySelectors.isAlertNotificationEnabled(state),
  alertNotificationRowNumbers: discoverySelectors.getAlertNotificationRowNumbers(state),
  alertNotificationPollingTime: discoverySelectors.getAlertNotificationPollingTime(state),
  isAlertNotificationNewIPEnabled: discoverySelectors.isAlertNotificationNewIPEnabled(state),
  isAlertNotificationBufferingEnabled: discoverySelectors.isAlertNotificationBufferingEnabled(state),
  isAlertNotificationAuditDiscrepancyEnabled: discoverySelectors.isAlertNotificationAuditDiscrepancyEnabled(state),
  isAlertNotificationInterceptDynamicIPLookupEnabled:
    discoverySelectors.isAlertNotificationInterceptDynamicIPLookupEnabled(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  pollAlertNotifications: alertNotificationActions.pollAlertNotifications,
  deleteAlertNotification: alertNotificationActions.deleteAlertNotification,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux;

export const AlertNotification: FunctionComponent<Props> = ({
  isAuthenticated,
  deliveryFunctionId,
  alertNotifications,
  isAlertNotificationEnabled,
  alertNotificationRowNumbers,
  alertNotificationPollingTime,
  isAlertNotificationNewIPEnabled,
  isAlertNotificationBufferingEnabled,
  isAlertNotificationAuditDiscrepancyEnabled,
  isAlertNotificationInterceptDynamicIPLookupEnabled,
  isAuditEnabled,
  userId,
  userName,
  pollAlertNotifications,
  deleteAlertNotification,
}: Props): ReactElement | null => {
  const [collapsed, setCollapsed] = useState(false);
  const previousAlertNotifications = useRef<AlertNotificationModel[]>();
  const { t } = useTranslation();

  useEffect(() => {
    if (isAlertNotificationEnabled) {
      const alarmTypes = getAlarmTypes(
        isAlertNotificationNewIPEnabled,
        isAlertNotificationBufferingEnabled,
        isAlertNotificationAuditDiscrepancyEnabled,
        isAlertNotificationInterceptDynamicIPLookupEnabled,
      );
      if (alarmTypes.length > 0) {
        pollAlertNotifications(
          deliveryFunctionId,
          alertNotificationPollingTime,
          alarmTypes,
          alertNotificationRowNumbers,
          'utcDateTime desc',
        );
      }
    }
  }, [
    isAlertNotificationEnabled,
    alertNotificationRowNumbers,
    isAlertNotificationNewIPEnabled,
    isAlertNotificationBufferingEnabled,
    isAlertNotificationAuditDiscrepancyEnabled,
    isAlertNotificationInterceptDynamicIPLookupEnabled,
    deliveryFunctionId,
    alertNotificationPollingTime,
    pollAlertNotifications,
  ]);

  useEffect(() => {
    if (!isEqual(previousAlertNotifications.current, alertNotifications)) {
      previousAlertNotifications.current = alertNotifications;
      setCollapsed(false);
    }
  }, [
    alertNotifications,
  ]);

  const onCollapseExpandClick = () => setCollapsed(!collapsed);

  const onMouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => event.stopPropagation();

  const clearAlert = (id: string): void => {
    if (id == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };
    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        id,
        AuditService.KEY_ALARM,
        AuditActionType.KEY_ALARM_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }
    deleteAlertNotification(id, body);
  }

  const renderAlarms = () => {
    if (collapsed) {
      return null;
    }
    const alerts: JSX.Element[] = [];
    alertNotifications.map(({ id, message }) => {
      const temp = window.atob(id).split('#');
      alerts.push(
        <div key={id} className="alert-notification-item">
          <div>
            Case Id: {temp[1]}, IP address: {message} has changed
          </div>
          { temp[2] === 'NEWIP' ?
          (
            <div>
              <Button icon="cross" minimal onClick={() => clearAlert(id)} />
            </div>
          ) : null}
        </div>
      );
    });
    return alerts;
  };

  if (!isAuthenticated || alertNotifications.length === 0 || !isAlertNotificationEnabled) {
    return null;
  }

  return (
    <Draggable>
      <div className="alert-notification" data-testid="AlertNotification">
        <div className="bp3-callout bp3-intent-danger bp3-callout-icon">
          <Icon icon="notifications" iconSize={20} intent={Intent.DANGER} />
          <h4 className="bp3-heading">{t('alertNotification.title', { length: alertNotifications.length })}</h4>
          <AlertNotificationCollapseExpand collapsed={collapsed} onClick={onCollapseExpandClick} />
          <div className="alert-notification-content" onMouseDown={onMouseDown} role="presentation">
            {renderAlarms()}
          </div>
        </div>
      </div>
    </Draggable>
  );
};

export default (connector(AlertNotification));
